import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAllTopicFillinQA,
  updateTopicFillinQA,
  deleteTopicFillinQA
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import AdminTable from './AdminTable';
import { grammarCategoriesField } from './GrammarCategoriesField';

const TopicFillinQAManager = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const response = await fetchAllTopicFillinQA();
      setExercises(response.exercises || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching topic fill-in Q&A:', err);
      setError('Failed to load topic fill-in Q&A');
      setExercises([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercises();
  }, []);

  const uniqueTopics = useMemo(() => {
    return [...new Set(exercises.map((item) => item.topic))].filter(Boolean);
  }, [exercises]);

  const handleSave = async (id, data) => {
    await updateTopicFillinQA(id, data);
    setExercises((prev) => prev.map((ex) => (ex.id === id ? { ...ex, ...data } : ex)));
  };

  const handleDelete = async (id) => {
    await deleteTopicFillinQA(id);
    setExercises((prev) => prev.filter((ex) => ex.id !== id));
  };

  const columns = [
    { key: 'id', header: 'ID', width: 60 },
    { key: 'topic', header: 'Topic', editable: true, width: 140 },
    { key: 'qa_order', header: 'Order', width: 70 },
    { key: 'question', header: 'Question', editable: true, width: 240 },
    { key: 'answer', header: 'Answer', editable: true, width: 240 },
    { key: 'grammar_categories', header: 'Grammar Categories', editable: true, width: 150,
      render: (value) => Array.isArray(value) ? value.join(', ') : (value || ''),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: 100,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  const editableFields = [
    { key: 'topic', type: 'select', label: 'Topic', required: true, options: uniqueTopics },
    { key: 'question', type: 'textarea', label: 'Question', minHeight: '60px', required: true },
    { key: 'answer', type: 'textarea', label: 'Answer', minHeight: '60px', required: true },
    grammarCategoriesField,
  ];

  if (loading && exercises.length === 0) {
    return <Loading />;
  }

  if (error && exercises.length === 0) {
    return <Error message={error} />;
  }

  return (
    <div>
      {/* Otázky jsou globální podle tématu — úprava/smazání ovlivní všechny
          uživatele a nic se už negeneruje automaticky. */}
      <div style={{
        marginBottom: '20px', padding: '12px 16px', borderRadius: '6px',
        backgroundColor: '#fff3cd', border: '1px solid #ffeeba', color: '#856404'
      }}>
        <p style={{ margin: 0, fontSize: '0.9rem' }}>
          Otázky doplňování jsou společné pro všechny uživatele daného tématu.
          Smazané otázky se negenerují znovu — téma bez otázek zůstane pro studenty prázdné.
        </p>
      </div>

      <AdminTable
        data={exercises}
        columns={columns}
        onSave={handleSave}
        onDelete={handleDelete}
        editableFields={editableFields}
        title="Doplňování"
        filterField="topic"
        uniqueFilterValues={uniqueTopics}
        loading={loading}
        emptyMessage="No topic fill-in Q&A found."
      />
    </div>
  );
};

export default TopicFillinQAManager;
